/**
 * Replay viewer. The server rebuilds a finished match from its action log and returns one redacted
 * view per step (the viewer's own seat — the same information they had at the table). We feed those
 * frames into the game store read-only (no legal actions) and reuse the live table components.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { GameTypeDto, SeatPublic } from '@cardhub/protocol';
import { api } from '../api/client';
import { ErrorState, Icon, Loading } from '../components/ui';
import { CallBreakTable } from '../games/CallBreakTable';
import { SpadesTable } from '../games/SpadesTable';
import { TarneebTable } from '../games/TarneebTable';
import { DehlaPakadTable } from '../games/DehlaPakadTable';
import { HeartsTable } from '../games/HeartsTable';
import { FiveHundredTable } from '../games/FiveHundredTable';
import { JudgementTable } from '../games/JudgementTable';
import { RummyTable } from '../games/RummyTable';
import { EuchreTable } from '../games/EuchreTable';
import { GinTable } from '../games/GinTable';
import { TwentyNineTable } from '../games/TwentyNineTable';
import { CrazyEightsTable } from '../games/CrazyEightsTable';
import { CribbageTable } from '../games/CribbageTable';
import { BlackjackTable } from '../games/BlackjackTable';
import { BhabhiTable } from '../games/BhabhiTable';
import { PinochleTable } from '../games/PinochleTable';
import { BridgeTable } from '../games/BridgeTable';
import { SevensTable } from '../games/SevensTable';
import { ScopaTable } from '../games/ScopaTable';
import { SeepTable } from '../games/SeepTable';
import { WhistTable } from '../games/WhistTable';
import { BluffTable } from '../games/BluffTable';
import { CourtPieceTable } from '../games/CourtPieceTable';
import { BaccaratTable } from '../games/BaccaratTable';
import { DragonTigerTable } from '../games/DragonTigerTable';
import { AndarBaharTable } from '../games/AndarBaharTable';
import { MarriageTable } from '../games/MarriageTable';
import { PokerTable } from '../games/PokerTable';
import { TeenPattiTable } from '../games/TeenPattiTable';
import { useT } from '../i18n';
import { errorMessage } from '../lib/hooks';
import { useGame, type GameEvent } from '../store/game';

interface ReplayPlayer { seat: number; userId: string; username: string; avatar: string; isBot: boolean; place: number | null; coinsDelta: number }
interface ReplayFrame { actor: string; kind: string; seat: number | null; events: GameEvent[]; view: { turn?: number; seat: number | null; players?: ({ playerId: string; chips: number } | null)[] } }
interface ReplayData { matchId: string; gameType: GameTypeDto; seat: number | null; players: ReplayPlayer[]; frames: ReplayFrame[] }

const SPEEDS = [1, 2, 4] as const;

export function Replay() {
  const { matchId = '' } = useParams();
  const t = useT();
  const nav = useNavigate();
  const [data, setData] = useState<ReplayData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);
  const [scoresOpen, setScoresOpen] = useState(false);
  const eventsId = useRef(0);
  const storeTable = useGame((s) => s.table?.tableId);

  const load = useCallback(() => {
    setError(null);
    api
      .get<ReplayData>(`/api/v1/matches/${matchId}/replay`)
      .then((d) => {
        setData(d);
        setI(Math.min(1, d.frames.length - 1));
        setPlaying(true);
      })
      .catch((e) => setError(errorMessage(e)));
  }, [matchId]);
  useEffect(load, [load]);
  useEffect(() => () => useGame.getState().reset(), []);

  // Project the current frame into the (read-only) game store.
  useEffect(() => {
    if (!data) return;
    const f = data.frames[i];
    if (!f) return;
    const byId = new Map(data.players.map((p) => [p.userId, p]));
    const seatCount = f.view.players?.length ?? data.players.length;
    const seats: SeatPublic[] = Array.from({ length: seatCount }, (_, s) => {
      const id = f.view.players ? f.view.players[s]?.playerId : data.players.find((p) => p.seat === s)?.userId;
      const p = id ? byId.get(id) : undefined;
      return {
        seat: s,
        user: p ? { id: p.userId, username: p.username, avatar: p.avatar, level: 1, isBot: p.isBot } : null,
        isBot: p?.isBot ?? false,
        connected: true,
        botControlled: false,
        ...(f.view.players?.[s] ? { chips: f.view.players[s]!.chips } : {}),
      };
    });
    eventsId.current++;
    useGame.setState({
      table: { tableId: `replay-${data.matchId}`, matchId: data.matchId, gameType: data.gameType, tableConfigId: '', name: t('replay.title'), entryFee: 0, status: 'IN_PROGRESS', privacy: 'PRIVATE', roomCode: null, maxPlayers: seatCount },
      mySeat: f.view.seat,
      seats,
      view: f.view,
      legal: null,
      turn: { seats: typeof f.view.turn === 'number' && f.view.turn >= 0 ? [f.view.turn] : [], deadline: null, turnMs: 0 },
      seq: i,
      events: f.events,
      eventsId: eventsId.current,
      result: null,
      closed: null,
      mm: null,
    });
  }, [data, i, t]);

  // Auto-advance; system steps (trick collection, dealing) move faster than player decisions.
  useEffect(() => {
    if (!playing || !data) return;
    if (i >= data.frames.length - 1) {
      setPlaying(false);
      return;
    }
    const next = data.frames[i + 1]!;
    const ms = (next.kind === 'action' ? 900 : 450) / speed;
    const h = setTimeout(() => setI((x) => Math.min(x + 1, data.frames.length - 1)), ms);
    return () => clearTimeout(h);
  }, [playing, i, data, speed]);

  if (error) return <main className="page"><ErrorState message={error} onRetry={load} /></main>;
  // The board reads the store, which is filled by the effect above after the first render.
  if (!data || storeTable !== `replay-${data.matchId}`) return <div className="table-screen" style={{ display: 'grid', placeItems: 'center' }}><Loading /></div>;

  const last = data.frames.length - 1;
  const me = data.players.find((p) => p.seat === data.seat);
  return (
    <div className="table-screen" data-game={data.gameType}>
      <header className="table-top">
        <button className="icon-btn" onClick={() => nav(-1)} aria-label={t('common.back')}>
          <Icon name="back" />
        </button>
        <div className="grow">
          <div className="title ellipsis">{t('replay.title')}</div>
          <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
            {t(`game.${data.gameType}` as 'game.CALL_BREAK')}
            {me?.place ? ` · #${me.place}` : ''}
          </div>
        </div>
        {(data.gameType === 'CALL_BREAK' || data.gameType === 'SPADES' || data.gameType === 'TARNEEB' || data.gameType === 'HEARTS' || data.gameType === 'FIVE_HUNDRED' || data.gameType === 'JUDGEMENT' || data.gameType === 'RUMMY' || data.gameType === 'EUCHRE' || data.gameType === 'GIN_RUMMY' || data.gameType === 'TWENTY_NINE' || data.gameType === 'CRAZY_EIGHTS' || data.gameType === 'CRIBBAGE' || data.gameType === 'BHABHI' || data.gameType === 'PINOCHLE' || data.gameType === 'BRIDGE' || data.gameType === 'SEVENS' || data.gameType === 'SCOPA' || data.gameType === 'BLUFF' || data.gameType === 'COURT_PIECE' || data.gameType === 'DEHLA_PAKAD' || data.gameType === 'WHIST' || data.gameType === 'SEEP' || data.gameType === 'BELOTE' || data.gameType === 'DURAK' || data.gameType === 'PRESIDENT' || data.gameType === 'BRISCOLA' || data.gameType === 'GO_FISH' || data.gameType === 'TRESSETTE' || data.gameType === 'CANASTA' || data.gameType === 'SKAT' || data.gameType === 'BIG_TWO' || data.gameType === 'WAR' || data.gameType === 'RUMMY_500' || data.gameType === 'PITCH' || data.gameType === 'HAZARI') && (
          <button className="icon-btn" onClick={() => setScoresOpen(true)} aria-label={t('table.scores')}>
            <Icon name="trophy" />
          </button>
        )}
      </header>

      <div className="table-area">
        {data.gameType === 'CALL_BREAK' && <CallBreakTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'SPADES' && <SpadesTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'DEHLA_PAKAD' && <DehlaPakadTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'TARNEEB' && <TarneebTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'HEARTS' && <HeartsTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'FIVE_HUNDRED' && <FiveHundredTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'JUDGEMENT' && <JudgementTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'RUMMY' && <RummyTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'EUCHRE' && <EuchreTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'GIN_RUMMY' && <GinTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'TWENTY_NINE' && <TwentyNineTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'CRAZY_EIGHTS' && <CrazyEightsTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'CRIBBAGE' && <CribbageTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {(data.gameType === 'TEEN_PATTI' || data.gameType === 'FLUSH') && <TeenPattiTable />}
        {data.gameType === 'BLACKJACK' && <BlackjackTable />}
        {data.gameType === 'ANDAR_BAHAR' && <AndarBaharTable />}
        {data.gameType === 'BACCARAT' && <BaccaratTable />}
        {data.gameType === 'DRAGON_TIGER' && <DragonTigerTable />}
        {data.gameType === 'BHABHI' && <BhabhiTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'PINOCHLE' && <PinochleTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'BRIDGE' && <BridgeTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'SEVENS' && <SevensTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'SCOPA' && <ScopaTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'SEEP' && <SeepTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'WHIST' && <WhistTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'BLUFF' && <BluffTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'COURT_PIECE' && <CourtPieceTable scoresOpen={scoresOpen} onCloseScores={() => setScoresOpen(false)} />}
        {data.gameType === 'POKER' && <PokerTable />}
        {data.gameType === 'MARRIAGE' && <MarriageTable />}
      </div>

      <div className="replay-bar" role="group" aria-label={t('replay.controls')}>
        <button className="icon-btn" onClick={() => { setPlaying(false); setI(0); }} aria-label={t('replay.start')} disabled={i === 0}>⏮</button>
        <button className="icon-btn" onClick={() => { setPlaying(false); setI((x) => Math.max(0, x - 1)); }} aria-label={t('replay.prev')} disabled={i === 0}>◀</button>
        <button className="icon-btn primary" onClick={() => { if (i >= last) setI(0); setPlaying((p) => !p || i >= last); }} aria-label={playing ? t('replay.pause') : t('replay.play')} aria-pressed={playing}>
          {playing ? '⏸' : '▶'}
        </button>
        <button className="icon-btn" onClick={() => { setPlaying(false); setI((x) => Math.min(last, x + 1)); }} aria-label={t('replay.next')} disabled={i >= last}>▶▶</button>
        <input
          className="grow"
          type="range"
          min={0}
          max={last}
          value={i}
          onChange={(e) => { setPlaying(false); setI(Number(e.target.value)); }}
          aria-label={t('replay.position')}
          aria-valuetext={t('replay.step', { n: i + 1, total: last + 1 })}
        />
        <button className="btn sm" onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]!)} aria-label={t('replay.speed')}>
          {speed}×
        </button>
      </div>
    </div>
  );
}
